// Keeps in-world chat bubbles from covering each other.
//
// Every bubble is pinned to its speaker (up and to the right of the head, see AvatarHud.spokeImpl),
// and a bubble is about five tiles wide, so two people talking a few tiles apart used to land one
// panel squarely on the other -- around a camp fire, with the Lord and a couple of recruits all
// talking, that was the normal case rather than the exception. Once a frame the renderer hands the
// open bubbles to arrangeBubbles(), which decides where each one sits:
//
//  - The bubble already being read keeps its place: older lines are placed first, and a HELD line
//    (one waiting on the player's Next) before everything, so the one they are working through never
//    moves out from under them.
//  - A newer line that would collide takes the cheapest free spot among: sliding sideways a little,
//    flipping to the speaker's LEFT (tail mirrored), or lifting straight up until it clears. The tail
//    tip never moves -- it stays over the speaker's head and the tail stretches to reach it.
//  - A spot whose tail would cross another bubble's tail costs extra, because crossed tails are the
//    one thing that makes it genuinely unclear who said what.
//  - A fading line is still drawn where it is but no longer claims space -- it is leaving, and a new
//    line should not be shoved aside for it.
//  - Bubbles stay on the SCREEN. A speaker near the right edge has their bubble slide or flip
//    inward; one near the top edge has it DROP beside their head (the only way down from above the
//    head -- AvatarHud then turns the tail to come out of the bubble's side). A speaker who is
//    themselves off the screen is left alone: the roster's speech lane already carries their line.
//  - A DROPPED bubble keeps off everyone else's head: a drop brings the body down to head height,
//    right where the person standing next to its speaker has their face. (Only drops: a bubble at
//    rest or risen is above the heads on its own row, and making heads block those too would shove
//    an older bubble -- the one being read -- aside for a newer speaker who walked in under it.)
//  - If there is no room anywhere, on screen beats clear of the others: the bubble takes the
//    cheapest on-screen spot and draws OVER whatever it lands on (newest on top), so the line that
//    just arrived is at least readable.
//
// The maths is pure and works in a common unit space (HUD-local units, shifted by each HUD's origin),
// which is what makes it unit-testable without a renderer. AvatarHud does the drawing and the easing.

export type BubbleSide = "right" | "left"

export interface BubblePlacement {
    side: BubbleSide
    // How far the body is slid sideways (+ is right), in HUD units.
    dx: number
    // How far the body is raised above its resting spot, in HUD units.
    lift: number
}

export interface BubbleRect {
    x: number
    y: number
    w: number
    h: number
}

export interface BubblePoint {
    x: number
    y: number
}

export interface BubbleSlot {
    // When the line opened; lower is older. Older lines are placed first and keep their spot.
    order: number
    held: boolean
    fading: boolean
    // The body (tail excluded) at rest on each side of the speaker.
    right: BubbleRect
    left: BubbleRect
    // The tail on each side: the x of the middle of its base at rest (the base is on the body's bottom
    // edge and moves with it), and its tip, which is fixed over the speaker's head.
    tailBaseX: { right: number; left: number }
    tip: { right: BubblePoint; left: BubblePoint }
    // Where the bubble is now, if it has been placed before -- preferred while it still fits, so a
    // bubble does not hop around as its neighbours come and go.
    current?: BubblePlacement
    // The visible screen, in the same units. Absent = no edges to keep to.
    bounds?: BubbleRect
    // The speaker's head (and the role glyph's lower half above it), which other bubbles keep off
    // when they drop.
    head?: BubbleRect
    // What this bubble keeps clear of as if it were another bubble: the player an INVITATION is
    // talking to (the feedback rabbit's question), whose face it must not cover.
    avoid?: BubbleRect[]
}

export const DEFAULT_PLACEMENT: BubblePlacement = { side: "right", dx: 0, lift: 0 }

// Clear air kept between two bubbles.
export const BUBBLE_GAP = 4
// The highest a bubble will climb to get clear -- about one bubble's height. Past this the tail is
// so long it no longer reads as belonging to anyone, and an overlap (newest on top) is the lesser evil.
export const MAX_BUBBLE_LIFT = 120
// The furthest a bubble will slide sideways. Past this its tail is nearly horizontal.
export const MAX_BUBBLE_SHIFT = 48
// Clear air kept between a bubble and the screen's edge.
export const BUBBLE_EDGE_MARGIN = 4
// Costs, in units of movement (1 unit of lift or slide costs 1). Leaving the bubble's current side
// costs a flat FLIP_COST, so a nudge of a few units beats a flip and a climb over a whole neighbour
// does not. A spot whose tail crosses another's costs CROSS_COST on top -- more than the highest
// climb, so crossed tails are only ever the last resort. (The case that needs it: a newer speaker a
// tile to the RIGHT of an older one. Flipping puts each bubble over the OTHER speaker with their tails
// in an X; stacking above, tail tucked behind the older panel, reads correctly.)
export const FLIP_COST = 24
export const CROSS_COST = MAX_BUBBLE_LIFT + FLIP_COST

const samePlacement = (a: BubblePlacement | undefined, b: BubblePlacement) =>
    !!a && a.side === b.side && a.dx === b.dx && a.lift === b.lift

export const bubbleRectAt = (slot: BubbleSlot, p: BubblePlacement): BubbleRect => {
    const body = slot[p.side]
    return { x: body.x + p.dx, y: body.y - p.lift, w: body.w, h: body.h }
}

interface TailSegment {
    x1: number
    y1: number
    x2: number
    y2: number
}

// The tail as a segment, for the crossing test -- or null once a dropped body has come down past its
// own tip, where AvatarHud draws a short tail out of the bubble's side instead (or none).
const tailAt = (slot: BubbleSlot, p: BubblePlacement): TailSegment | null => {
    const body = slot[p.side]
    const tip = slot.tip[p.side]
    const baseY = body.y + body.h - p.lift
    if (tip.y <= baseY) {
        return null
    }
    return { x1: slot.tailBaseX[p.side] + p.dx, y1: baseY, x2: tip.x, y2: tip.y }
}

export const bubblesOverlap = (a: BubbleRect, b: BubbleRect): boolean =>
    a.x < b.x + b.w + BUBBLE_GAP && b.x < a.x + a.w + BUBBLE_GAP &&
    a.y < b.y + b.h + BUBBLE_GAP && b.y < a.y + a.h + BUBBLE_GAP

// Do two tails properly cross (not merely touch at an end)?
const tailsCross = (a: TailSegment | null, b: TailSegment | null): boolean => {
    if (!a || !b) {
        return false
    }
    const side = (px: number, py: number, s: TailSegment) => Math.sign((s.x2 - s.x1) * (py - s.y1) - (s.y2 - s.y1) * (px - s.x1))
    return side(a.x1, a.y1, b) * side(a.x2, a.y2, b) < 0 && side(b.x1, b.y1, a) * side(b.x2, b.y2, a) < 0
}

interface Placed {
    rect: BubbleRect
    tail: TailSegment | null
}

const contains = (outer: BubbleRect, r: BubbleRect) =>
    r.x >= outer.x && r.y >= outer.y && r.x + r.w <= outer.x + outer.w && r.y + r.h <= outer.y + outer.h

const insetBy = (r: BubbleRect, m: number): BubbleRect => ({ x: r.x + m, y: r.y + m, w: r.w - m * 2, h: r.h - m * 2 })

// The screen this slot has to keep to, or null. A speaker whose head is itself off the screen gets
// no edges: pulling their bubble in would show a line the roster lane is already showing, with a
// tail pointing at nobody.
const screenFor = (slot: BubbleSlot): BubbleRect | null => {
    if (!slot.bounds) {
        return null
    }
    const screen = insetBy(slot.bounds, BUBBLE_EDGE_MARGIN)
    const tip = slot.tip.right
    const onScreen = tip.x >= screen.x && tip.x <= screen.x + screen.w && tip.y >= screen.y && tip.y <= screen.y + screen.h
    return onScreen ? screen : null
}

const choosePlacement = (slot: BubbleSlot, placed: Placed[], heads: BubbleRect[]): BubblePlacement => {
    const home = slot.current?.side ?? DEFAULT_PLACEMENT.side
    const screen = screenFor(slot)
    const candidates: BubblePlacement[] = []
    for (const side of ["right", "left"] as BubbleSide[]) {
        const body = slot[side]
        // Sideways and vertical offsets worth trying, then every pairing of the two -- a bubble in a
        // corner can need a drop AND a flip or a slide.
        const dxs = [0]
        const lifts = [0]
        // Each bubble already placed -- and anything this one must avoid -- offers three ways round
        // it: just above it, or just clear of it to either side.
        for (const o of [...placed.map(p => p.rect), ...(slot.avoid || [])]) {
            const above = Math.ceil(body.y + body.h - (o.y - BUBBLE_GAP))
            if (above > 0 && above <= MAX_BUBBLE_LIFT) {
                lifts.push(above)
            }
            const right = Math.ceil(o.x + o.w + BUBBLE_GAP - body.x)
            if (right > 0 && right <= MAX_BUBBLE_SHIFT) {
                dxs.push(right)
            }
            const left = Math.floor(o.x - BUBBLE_GAP - (body.x + body.w))
            if (left < 0 && left >= -MAX_BUBBLE_SHIFT) {
                dxs.push(left)
            }
        }
        // ...and the screen's edges offer the offsets that just bring it back inside.
        if (screen) {
            const inFromRight = Math.floor(screen.x + screen.w - (body.x + body.w))
            if (inFromRight < 0 && inFromRight >= -MAX_BUBBLE_SHIFT) {
                dxs.push(inFromRight)
            }
            const inFromLeft = Math.ceil(screen.x - body.x)
            if (inFromLeft > 0 && inFromLeft <= MAX_BUBBLE_SHIFT) {
                dxs.push(inFromLeft)
            }
            // Down from the top edge: a negative lift. At most until the body's top reaches the tail
            // tip -- it then sits beside the head, and lower than that it would be beside the knees.
            const downFromTop = Math.floor(body.y - screen.y)
            const maxDrop = slot.tip[side].y - body.y
            if (downFromTop < 0 && -downFromTop <= maxDrop) {
                lifts.push(downFromTop)
            }
            const upFromBottom = Math.ceil(body.y + body.h - (screen.y + screen.h))
            if (upFromBottom > 0 && upFromBottom <= MAX_BUBBLE_LIFT) {
                lifts.push(upFromBottom)
            }
        }
        for (const dx of dxs) {
            for (const lift of lifts) {
                candidates.push({ side, dx, lift })
            }
        }
    }
    if (slot.current) {
        candidates.push(slot.current)
    }

    // Cheapest spot that is on the screen and clear of everything placed (and, for a drop, of everyone
    // else's head); failing that, the cheapest on the screen at all (it will overlap -- newest on top
    // keeps it readable).
    let clear: BubblePlacement | undefined
    let clearCost = Infinity
    let onScreen: BubblePlacement | undefined
    let onScreenCost = Infinity
    for (const p of candidates) {
        const rect = bubbleRectAt(slot, p)
        if (screen && !contains(screen, rect)) {
            continue
        }
        const tail = tailAt(slot, p)
        const cost = Math.abs(p.lift) + Math.abs(p.dx) + (p.side === home ? 0 : FLIP_COST) +
            (placed.some(o => tailsCross(tail, o.tail)) ? CROSS_COST : 0)
        // On a tie, the spot the bubble already holds wins.
        const better = (best: number) => cost < best || (cost === best && samePlacement(slot.current, p))
        if (better(onScreenCost)) {
            onScreen = p
            onScreenCost = cost
        }
        const onAFace = p.lift < 0 && heads.some(h => bubblesOverlap(rect, h))
        const onAvoided = !!slot.avoid?.some(a => bubblesOverlap(rect, a))
        if (!placed.some(o => bubblesOverlap(rect, o.rect)) && !onAFace && !onAvoided && better(clearCost)) {
            clear = p
            clearCost = cost
        }
    }
    const pick = clear ?? onScreen
    if (pick) {
        return pick
    }
    // Nothing in reach is on the screen (a bubble wider than the slide limit is off the edge): pull
    // the bubble's home spot inside the screen by whatever it takes.
    const fallback = slot.current ?? DEFAULT_PLACEMENT
    return screen ? clampInto(slot, fallback, screen) : fallback
}

const clampInto = (slot: BubbleSlot, p: BubblePlacement, screen: BubbleRect): BubblePlacement => {
    const r = bubbleRectAt(slot, p)
    const dx = r.x < screen.x ? screen.x - r.x : r.x + r.w > screen.x + screen.w ? screen.x + screen.w - (r.x + r.w) : 0
    const dy = r.y < screen.y ? screen.y - r.y : r.y + r.h > screen.y + screen.h ? screen.y + screen.h - (r.y + r.h) : 0
    return { side: p.side, dx: Math.round(p.dx + dx), lift: Math.round(p.lift - dy) }
}

// Where each bubble goes this frame, index-aligned with `slots`.
export const arrangeBubbles = (slots: BubbleSlot[]): BubblePlacement[] => {
    const byPriority = slots.map((_, i) => i).sort((a, b) =>
        Number(slots[b].held) - Number(slots[a].held) || slots[a].order - slots[b].order)
    const placed: Placed[] = []
    const placements: BubblePlacement[] = new Array(slots.length)
    for (const i of byPriority) {
        const slot = slots[i]
        // Every OTHER speaker's head, placed yet or not: heads do not move.
        const heads = slots.filter((other, j) => j !== i && other.head).map(other => other.head!)
        const placement = slot.fading ? slot.current ?? DEFAULT_PLACEMENT : choosePlacement(slot, placed, heads)
        placements[i] = placement
        if (!slot.fading) {
            placed.push({ rect: bubbleRectAt(slot, placement), tail: tailAt(slot, placement) })
        }
    }
    return placements
}

// Paint order of the open bubbles, index-aligned with `slots`: 1 is the bottom. Newest on top, so a
// line that could not find room is still readable over the one it lands on -- except that a LIFTED
// (risen, not dropped) bubble goes under every bubble at rest, because its body is clear of them by construction and only
// its long tail crosses them; tucked behind, that tail reads as passing behind the other panel rather
// than as a stroke through its words.
export const bubblePaintRanks = (slots: BubbleSlot[], placements: BubblePlacement[]): number[] => {
    const order = slots.map((_, i) => i).sort((a, b) =>
        Number(placements[a].lift <= 0) - Number(placements[b].lift <= 0) || slots[a].order - slots[b].order)
    const ranks: number[] = new Array(slots.length)
    order.forEach((slotIndex, rank) => {
        ranks[slotIndex] = rank + 1
    })
    return ranks
}

// One open bubble, as the frame loop sees it (AvatarHud implements this).
export interface LaidOutBubble {
    // This frame's slot, or null while the bubble is not on screen (culled, hidden). `screen` is the
    // visible area in the renderer's global coordinates, for the slot to carry as its bounds.
    // `keepClear` (same coordinates) is what an invitation's bubble should keep off: see BubbleSlot.avoid.
    bubbleSlot(screen?: BubbleRect, keepClear?: BubbleRect[]): BubbleSlot | null
    applyBubblePlacement(placement: BubblePlacement, paintRank: number, now: number): void
    animateBubblePlacement(now: number): void
    // Where the bubble is drawn, in the renderer's global coordinates, or null while it is not.
    bubbleScreenRect?(): BubbleRect | null
    // An invitation the player may ignore (SpeechOptions.invitation): it does not make the screen busy.
    quietExempt?(): boolean
}

class SpeechBubbleRegistry {
    private open = new Set<LaidOutBubble>()
    private sequence = 0

    // A line has just opened on this bubble. Returns its order -- newer lines get higher numbers, so
    // re-opening (the speaker's next line) also makes it the newest.
    opened = (bubble: LaidOutBubble): number => {
        this.open.add(bubble)
        return ++this.sequence
    }

    closed = (bubble: LaidOutBubble) => {
        this.open.delete(bubble)
        // Nothing is open, so nothing's order needs comparing any more; start the count again rather
        // than letting it (and the paint ranks drawn from it) climb for the life of the session.
        if (!this.open.size) {
            this.sequence = 0
        }
    }

    openCount = () => this.open.size

    // Every bubble on screen right now, in global coordinates -- for HUD toasts that must not land on
    // somebody's line (chapter_celebration.ts).
    screenRects = (): BubbleRect[] => {
        const rects: BubbleRect[] = []
        this.open.forEach(bubble => {
            const rect = bubble.bubbleScreenRect?.()
            if (rect) rects.push(rect)
        })
        return rects
    }

    // Whether anybody is saying something on screen right now, invitations aside: what the quiet watch
    // asks before it calls the screen quiet (GuiManager). `screen` is the visible area, global coordinates.
    anyOnScreen = (screen: BubbleRect): boolean => {
        for (const bubble of this.open) {
            if (bubble.quietExempt?.()) continue
            const r = bubble.bubbleScreenRect?.()
            if (r && r.x < screen.x + screen.w && r.x + r.w > screen.x && r.y < screen.y + screen.h && r.y + r.h > screen.y) return true
        }
        return false
    }

    // Whether an invitation's bubble is open: the only kind that is handed `keepClear`.
    anyInvitationOpen = (): boolean => {
        for (const bubble of this.open) {
            if (bubble.quietExempt?.()) return true
        }
        return false
    }

    // Run once a frame, from the renderer. `screen` is the visible area in global coordinates, and
    // `keepClear` what an invitation's bubble keeps off (the player's own body).
    arrange = (now: number, screen?: BubbleRect, keepClear?: BubbleRect[]) => {
        if (!this.open.size) {
            return
        }
        const members: LaidOutBubble[] = []
        const slots: BubbleSlot[] = []
        this.open.forEach(bubble => {
            const slot = bubble.bubbleSlot(screen, keepClear)
            if (slot) {
                members.push(bubble)
                slots.push(slot)
            }
        })
        const placements = arrangeBubbles(slots)
        const ranks = bubblePaintRanks(slots, placements)
        members.forEach((bubble, i) => bubble.applyBubblePlacement(placements[i], ranks[i], now))
        this.open.forEach(bubble => bubble.animateBubblePlacement(now))
    }

    // Tests only: forget every bubble.
    clear = () => {
        this.open.clear()
        this.sequence = 0
    }
}

export const SpeechBubbleLayout = new SpeechBubbleRegistry()
